import React, { useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { flatten, isNil } from 'lodash';
import { cachedSearch, cachedSearchHash } from '../../actions/search';
import BasicTypeaheadPicker from './BasicTypeaheadPicker';
import useAPI from '../../hooks/useAPI';

const SearchTypeaheadPicker = ({
  initialOptions = [],
  searchResources = [],
  onChange,
  selectedValue = null,
  resourceTransformers = {},
  minTypeLength = 0,
  resultFilter = () => true,
  ...others
}) => {
  const [dropdownShowing, setDropdownShowing] = useState(false);
  const [query, setQuery] = useState('');
  const hash = cachedSearchHash(query, searchResources);
  const { data: searchResults, isLoading } = useAPI(
    () => cachedSearch(searchResources, query),
    [query],
    {
      autoFetch: true,
      selector: state => state.cachedSearch[hash],
    },
  );
  const onTypeAction = async value => {
    setDropdownShowing(value.length > minTypeLength);
    setQuery(value);
  };
  const onDropdownDismiss = () => setDropdownShowing(false);
  const onDropdownClick = () => setDropdownShowing(!dropdownShowing);
  const transformResource = key => resource => {
    return resourceTransformers[key] ? resourceTransformers[key](resource) : resource;
  };
  const aggregated = useMemo(
    () =>
      searchResults
        ? initialOptions.concat(
            flatten(searchResources.map(b => searchResults[b]?.map(transformResource(b)) || [])),
          )
        : initialOptions,
    [searchResults],
  );

  return (
    <BasicTypeaheadPicker
      options={aggregated}
      selectedValue={selectedValue}
      onChange={onChange}
      onType={onTypeAction}
      isLoading={isLoading && !isNil(aggregated)}
      showDropdown={dropdownShowing}
      onDropdownDismiss={onDropdownDismiss}
      onDropdownClick={onDropdownClick}
      resultFilter={resultFilter}
      prefixIcon="search"
      {...others}
    />
  );
};

SearchTypeaheadPicker.propTypes = {
  initialOptions: propTypes.arrayOf(
    propTypes.shape({
      key: propTypes.string.isRequired,
      text: propTypes.string.isRequired,
    }),
  ),
  searchResources: propTypes.arrayOf(propTypes.string),
  onChange: propTypes.func.isRequired,
  selectedValue: propTypes.shape({
    key: propTypes.any.isRequired,
    text: propTypes.string.isRequired,
  }),
  resourceTransformers: propTypes.object,
  minTypeLength: propTypes.number,
  resultFilter: propTypes.func,
};

SearchTypeaheadPicker.defaultProps = {
  initialOptions: [],
  searchResources: [],
  selectedValue: null,
  resourceTransformers: {},
  minTypeLength: 0,
  resultFilter: () => true,
};

export default SearchTypeaheadPicker;
